import {
	type ColumnDef,
	createColumnHelper,
	flexRender,
	getCoreRowModel,
	useReactTable,
	type VisibilityState
} from '@tanstack/react-table'
import {
	type ComponentProps,
	type ReactNode,
	useEffect,
	useRef,
	useState
} from 'react'
import { TableVirtuoso, type TableVirtuosoHandle } from 'react-virtuoso'
import { DEFAULT_PAGE_LIMIT } from '@/helpers/constants'
import type { Order } from '@/helpers/types'
import { cn } from '@/helpers/utils'
import { useTranslation } from '@/translations/i18n'
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle
} from './empty'
import { AlertCircleIcon, InfoCircleIcon } from './icons'
import {
	Pagination,
	PaginationContent,
	PaginationEllipsis,
	PaginationItem,
	PaginationLink,
	PaginationNext,
	PaginationPrevious
} from './pagination'
import {
	RowsPerPageSelect,
	type RowsPerPageValue
} from './rows-per-page-select'
import { Table } from './table'

interface ColumnMeta<TSortKey extends string> {
	sortKey?: TSortKey
	headerAlign?: 'start' | 'end'
}

type ColumnSize = number | `${number}%`

export interface DataTableProps<
	TDataItem,
	TSortKey extends string,
	TColumnId extends string
> {
	data: TDataItem[]
	columns: {
		id: TColumnId
		header: ReactNode
		/** Where the header label sits. `end` for a right-anchored column. */
		headerAlign?: 'start' | 'end'
		cell: ({ dataItem }: { dataItem: TDataItem }) => ReactNode
		size: ColumnSize
		maxSize?: ColumnSize
		minSize?: ColumnSize
		sortKey?: TSortKey
	}[]
	order?: Order<TSortKey>
	setOrder?: (order?: Order<TSortKey>) => void
	onEndReached?: () => void
	getRowProps?: ({
		dataItem
	}: {
		dataItem: TDataItem
	}) => ComponentProps<typeof Table.Row>
	isEmpty?: boolean
	isError?: boolean
	isLoading?: boolean
	lastPage?: number
	page?: number
	setPage?: (page: number) => void
	/**
	 * Current page size. Pass it together with `setRowsPerPage` to show the
	 * rows-per-page control; omit both to keep the bare pagination footer.
	 */
	rowsPerPage?: RowsPerPageValue
	setRowsPerPage?: (rowsPerPage: RowsPerPageValue) => void
	/** Total rows across every page — drives the "1-50 of 128" range label. */
	totalRows?: number
	columnPinning?: { left: NoInfer<TColumnId>[]; right: NoInfer<TColumnId>[] }
	columnVisibility?: Partial<Record<NoInfer<TColumnId>, boolean>> &
		VisibilityState
	emptyComponent?: ReactNode
	/**
	 * When set, the virtualized table scrolls against this external element
	 * (window/page scroll) and flows to its content height instead of scrolling
	 * inside a fixed-height region. The column header sticks to the top of that
	 * scroll parent. Omit for the default self-contained fixed-height scroll.
	 */
	customScrollParent?: HTMLElement
	/**
	 * When true, the table virtualizes against the window/document scroll and
	 * flows to its content height (the whole page scrolls). Mutually exclusive
	 * with `customScrollParent`.
	 */
	useWindowScroll?: boolean
}

const retrieveColumnSize = ({
	size,
	tableWidth
}: {
	size?: ColumnSize
	tableWidth: number
}) => {
	if (typeof size === 'string') {
		return (+size.replace('%', '') * tableWidth) / 100
	}
	return size
}

const makeReactTableColumns = <
	TDataItem,
	TSortKey extends string,
	TColumnId extends string
>({
	columns,
	tableWidth
}: {
	columns: DataTableProps<TDataItem, TSortKey, TColumnId>['columns']
	tableWidth: number
}): ColumnDef<TDataItem>[] => {
	const columnHelper = createColumnHelper<TDataItem>()

	return columns.map((column) =>
		columnHelper.display({
			id: column.id,
			header: () => column.header,
			cell: (cell) => column.cell({ dataItem: cell.row.original }),
			size: retrieveColumnSize({ size: column.size, tableWidth }),
			maxSize: retrieveColumnSize({ size: column.maxSize, tableWidth }),
			minSize: retrieveColumnSize({ size: column.minSize, tableWidth }),
			enableResizing: !!column.minSize || !!column.maxSize,
			meta: {
				sortKey: column.sortKey,
				headerAlign: column.headerAlign
			} as const satisfies ColumnMeta<TSortKey>
		})
	)
}

const getNextSortOrder = <TSortKey extends string>(
	currentOrder: Order<TSortKey> | undefined,
	sortKey: TSortKey
): Order<TSortKey> => {
	const isSameColumn = currentOrder?.orderBy === sortKey

	if (!(isSameColumn && currentOrder?.orderDirection)) {
		return { orderBy: sortKey, orderDirection: 'desc' }
	}

	if (currentOrder.orderDirection === 'desc') {
		return { orderBy: sortKey, orderDirection: 'asc' }
	}

	return { orderBy: undefined, orderDirection: undefined }
}

const buildPageNumbers = (
	currentPage: number,
	lastPage: number
): (number | 'ellipsis')[] => {
	if (lastPage <= 7) {
		return Array.from({ length: lastPage }, (_, i) => i + 1)
	}

	const pages: (number | 'ellipsis')[] = [1]

	if (currentPage <= 3) {
		pages.push(2, 3, 4, 5, 'ellipsis', lastPage)
	} else if (currentPage >= lastPage - 2) {
		pages.push(
			'ellipsis',
			lastPage - 4,
			lastPage - 3,
			lastPage - 2,
			lastPage - 1,
			lastPage
		)
	} else {
		pages.push(
			'ellipsis',
			currentPage - 1,
			currentPage,
			currentPage + 1,
			'ellipsis',
			lastPage
		)
	}

	return pages
}

function DataTable<
	TDataItem,
	TSortKey extends string,
	TColumnId extends string
>({
	data,
	columns,
	order,
	setOrder,
	onEndReached,
	getRowProps,
	isLoading,
	isError,
	lastPage,
	page = 1,
	setPage,
	rowsPerPage,
	setRowsPerPage,
	totalRows,
	columnPinning = { left: [], right: [] },
	columnVisibility,
	emptyComponent,
	customScrollParent,
	useWindowScroll
}: DataTableProps<TDataItem, TSortKey, TColumnId>) {
	const flows = !!customScrollParent || !!useWindowScroll
	const { t } = useTranslation()
	const tableRootRef = useRef<HTMLTableElement>(null)
	const virtuosoRef = useRef<TableVirtuosoHandle>(null)
	const [tableWidth, setTableWidth] = useState(0)

	const table = useReactTable({
		data,
		columnResizeMode: 'onChange',
		columns: makeReactTableColumns({ columns, tableWidth }),
		getCoreRowModel: getCoreRowModel(),
		state: {
			columnPinning,
			columnVisibility: columnVisibility ?? {}
		}
	})

	useEffect(() => {
		// Reset scroll on page change — but only for the self-contained scroller.
		// With window/parent scroll, scrolling the virtuoso hijacks the page and
		// would jump it down to the table on mount.
		if (page && !flows) {
			virtuosoRef.current?.scrollTo({
				top: 0,
				left: 0,
				behavior: 'instant'
			})
		}
	}, [page, flows])

	const { rows } = table.getRowModel()

	useEffect(() => {
		const tableVirtuosoElement = tableRootRef.current?.parentElement
		if (tableVirtuosoElement == null) {
			return
		}

		// A change this small is the vertical scrollbar arriving or leaving, not a
		// real resize, and reacting to it is what made expandable cells unusable:
		// the taller rows brought the scrollbar in, the narrower width rebuilt the
		// columns, react-table rebuilt its rows, every cell remounted and lost its
		// state — so the cell collapsed the instant it opened, the scrollbar went
		// away, and the width bounced back. Ignoring that band costs nothing,
		// since column sizes never need to be that exact.
		const SCROLLBAR_TOLERANCE = 20

		const measure = () => {
			const width = tableVirtuosoElement.clientWidth
			setTableWidth((current) =>
				Math.abs(width - current) <= SCROLLBAR_TOLERANCE
					? current
					: width
			)
		}
		const resizeObserver = new ResizeObserver(measure)
		resizeObserver.observe(tableVirtuosoElement)
		measure()

		return () => resizeObserver.disconnect()
	}, [])

	const hasPagination = !!setPage && !!lastPage && lastPage > 1
	const hasRowsPerPage = !!setRowsPerPage && rowsPerPage !== undefined
	const hasFooter = hasPagination || hasRowsPerPage

	// "1-50 of 128". The offset comes from the page size, but the end of the
	// range comes from the rows actually rendered, so a short last page reports
	// its real length instead of claiming rows the response never returned.
	// Falls back to the rendered count for tables that paginate without the
	// rows-per-page control, where the page size isn't passed in.
	const pageSize = rowsPerPage && rowsPerPage > 0 ? rowsPerPage : rows.length
	const rowsRange =
		totalRows && totalRows > 0 && rows.length > 0
			? {
					from: (page - 1) * pageSize + 1,
					to: Math.min(
						(page - 1) * pageSize + rows.length,
						totalRows
					),
					total: totalRows
				}
			: undefined

	const renderTableContent = () => {
		if (!isLoading && isError) {
			return (
				<Empty>
					<EmptyHeader>
						<EmptyMedia variant='icon'>
							<AlertCircleIcon className='fill-error-500' />
						</EmptyMedia>
						<EmptyTitle>{t('common:error_table_title')}</EmptyTitle>
						<EmptyDescription>
							{t('common:error_table_description')}
						</EmptyDescription>
					</EmptyHeader>
				</Empty>
			)
		}

		if (!isLoading && rows.length === 0) {
			return (
				emptyComponent ?? (
					<Empty>
						<EmptyHeader>
							<EmptyMedia variant='icon'>
								<InfoCircleIcon />
							</EmptyMedia>
							<EmptyTitle>
								{t('common:empty_table_title')}
							</EmptyTitle>
							<EmptyDescription>
								{t('common:empty_table_description')}
							</EmptyDescription>
						</EmptyHeader>
					</Empty>
				)
			)
		}

		return (
			<TableVirtuoso
				components={{
					// biome-ignore lint/correctness/noNestedComponentDefinitions: required by react-virtuoso
					Table: (props) => (
						<Table.Root
							className='h-full'
							ref={tableRootRef}
							{...props}
						/>
					),
					// biome-ignore lint/correctness/noNestedComponentDefinitions: required by react-virtuoso
					TableBody: ({ children, ...props }) => (
						<Table.Body {...props}>
							{isLoading
								? Array.from({
										// Cap the skeleton: a large page size
										// shouldn't paint hundreds of placeholder
										// rows nobody will scroll to.
										length: Math.min(
											rowsPerPage ?? DEFAULT_PAGE_LIMIT,
											DEFAULT_PAGE_LIMIT
										)
									}).map((_, rowIndex) =>
										table
											.getHeaderGroups()
											.map((headerGroup) => (
												<Table.Row
													key={`skeleton-${headerGroup.id}-${rowIndex}`}
												>
													{headerGroup.headers.map(
														(header) => {
															const pinningPosition =
																header.column.getIsPinned()
															return (
																<Table.Cell
																	key={
																		header.id
																	}
																	pinningPosition={
																		pinningPosition
																	}
																	pinningStart={header.column.getStart(
																		pinningPosition
																	)}
																>
																	<Table.CellSkeleton />
																</Table.Cell>
															)
														}
													)}
												</Table.Row>
											))
									)
								: children}
							{/* this row is needed to stretch the table to the full height if the rows height is less than the table wrapper height */}
							<tr />
						</Table.Body>
					),
					// biome-ignore lint/correctness/noNestedComponentDefinitions: required by react-virtuoso
					TableHead: ({ children, ...props }) => (
						<Table.Header {...props}>
							{children}
							<Table.HeaderGapRow
								columnsLength={
									table.getVisibleLeafColumns().length
								}
							/>
						</Table.Header>
					),
					// biome-ignore lint/correctness/noNestedComponentDefinitions: required by react-virtuoso
					TableRow: (props) => {
						const row = rows[props['data-index']]
						if (!row) {
							return null
						}
						const rowProps = getRowProps?.({
							dataItem: row.original
						})

						return (
							<Table.Row
								{...props}
								{...rowProps}
								className={cn(
									rowProps?.onClick && 'cursor-pointer',
									rowProps?.className
								)}
							>
								{row.getVisibleCells().map((cell) => (
									<Table.Cell
										key={cell.id}
										pinningPosition={cell.column.getIsPinned()}
										pinningStart={cell.column.getStart(
											cell.column.getIsPinned()
										)}
									>
										{flexRender(
											cell.column.columnDef.cell,
											cell.getContext()
										)}
									</Table.Cell>
								))}
							</Table.Row>
						)
					}
				}}
				customScrollParent={customScrollParent}
				endReached={onEndReached}
				fixedHeaderContent={() =>
					table.getHeaderGroups().map((headerGroup) => (
						<Table.HeaderRow key={headerGroup.id}>
							{headerGroup.headers.map((header) => {
								const meta = header.column.columnDef
									.meta as ColumnMeta<TSortKey>
								const sortKey = meta?.sortKey
								const pinningPosition =
									header.column.getIsPinned()

								return (
									<Table.HeaderCell
										colSpan={header.colSpan}
										key={header.id}
										labelAlign={meta?.headerAlign}
										pinningPosition={pinningPosition}
										pinningStart={header.column.getStart(
											pinningPosition
										)}
										style={{ width: header.getSize() }}
									>
										{header.isPlaceholder ? null : (
											<Table.HeaderCellLabel>
												{flexRender(
													header.column.columnDef
														.header,
													header.getContext()
												)}
											</Table.HeaderCellLabel>
										)}
										{!!sortKey && (
											<Table.SortButton
												onClick={() => {
													const nextOrder =
														getNextSortOrder(
															order,
															sortKey
														)
													setOrder?.(nextOrder)
												}}
												orderDirection={
													sortKey ===
														order?.orderBy &&
													order?.orderDirection
														? order?.orderDirection
														: null
												}
											/>
										)}
										{header.column.getCanResize() && (
											<Table.Resizer
												onDoubleClick={() =>
													header.column.resetSize()
												}
												onMouseDown={header.getResizeHandler()}
												onTouchStart={header.getResizeHandler()}
											/>
										)}
									</Table.HeaderCell>
								)
							})}
						</Table.HeaderRow>
					))
				}
				ref={virtuosoRef}
				totalCount={rows.length}
				useWindowScroll={useWindowScroll}
			/>
		)
	}

	return (
		<div
			className={
				flows
					? 'grid grid-rows-[auto_auto] gap-1'
					: 'grid h-full grid-rows-[minmax(0,1fr)_auto] gap-1'
			}
		>
			{renderTableContent()}
			{hasFooter ? (
				<div className='flex h-24 items-center justify-between gap-4 bg-neutral-white py-2.5'>
					{hasRowsPerPage ? (
						<div className='flex items-center gap-2'>
							<RowsPerPageSelect
								onChange={setRowsPerPage}
								value={rowsPerPage}
							/>
							{rowsRange && (
								<span className='shrink-0 whitespace-nowrap text-grey-700 text-sm/4.5'>
									{t('common:rows_range', rowsRange)}
								</span>
							)}
						</div>
					) : (
						// Keeps the page list centred when there is no left side.
						<span />
					)}

					{hasPagination ? (
						<Pagination className='mx-0 w-auto justify-end'>
							<PaginationContent>
								<PaginationItem>
									<PaginationPrevious
										disabled={page <= 1}
										onClick={() => {
											if (page > 1) {
												setPage(page - 1)
											}
										}}
									/>
								</PaginationItem>
								{buildPageNumbers(page, lastPage).map(
									(pageNum, index) =>
										pageNum === 'ellipsis' ? (
											<PaginationItem
												// biome-ignore lint/suspicious/noArrayIndexKey: ellipsis items don't have unique IDs
												key={`ellipsis-${index}`}
											>
												<PaginationEllipsis />
											</PaginationItem>
										) : (
											<PaginationItem key={pageNum}>
												<PaginationLink
													isActive={page === pageNum}
													onClick={() =>
														setPage(pageNum)
													}
													page={pageNum}
												>
													{pageNum}
												</PaginationLink>
											</PaginationItem>
										)
								)}
								<PaginationItem>
									<PaginationNext
										disabled={page >= lastPage}
										onClick={() => {
											if (page < lastPage) {
												setPage(page + 1)
											}
										}}
									/>
								</PaginationItem>
							</PaginationContent>
						</Pagination>
					) : (
						<span />
					)}
				</div>
			) : null}
		</div>
	)
}

export { DataTable }
